import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { commissionsActions } from '../store/commissions/slice';
import { useAppDispatch, useAppSelector } from './useAppStore';
import { QUERY_KEYS } from '../utils/enums';
import { getCommissionMode, getSelectedCommissionPlan } from '../store/commissions/selectors';
import { useAddCommissionsPlanAPI, useUpdateCommissionsPlanAPI } from './api/useCommissions';
import { TAddCommissionPlanReq } from '../types/requests';
import { ROUTES } from '../data';

type TPrePayload = {
  name: string,
  type: string,
  description: string,
};

export function useCommissions() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const commissionMode = useAppSelector(getCommissionMode);
  const { t } = useTranslation('commissions');

  const { mutateAsync: createPlanMutate, isLoading: isCreateLoading } = useAddCommissionsPlanAPI();
  const { mutateAsync: updatePlanMutate, isLoading: isUpdateLoading } = useUpdateCommissionsPlanAPI();

  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);

  const createCommission = async () => {
    dispatch(commissionsActions.setSelectedCommissionsPlan({
      type: 'Fixed',
      name: '',
      description: '',
      fixedType: 0,
      fixedAmount: '',
      productCommissions: [],
      commissionSteps: [
        {
          index: 0,
          from: 1,
          to: '',
          amount: '',
          percent: '',
        },
      ],
      bonusBase: 'Personal',
      bonuses: [
        {
          index: 0,
          name: '',
          from: '',
          fixedType: 'flat',
          fixedAmount: '',
        },
      ],
    }));

    dispatch(commissionsActions.setMemberEmails([]));

    dispatch(commissionsActions.setActiveStep(0));
    dispatch(commissionsActions.setCommissionMode('create'));
    navigate(ROUTES.CREATE_COMMISSIONS);
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMMISSIONS.GET_PLANS] });
  };

  const fixedTypesOptions = [
    { name: t('flat'), value: 0, id: 'flat' },
    { name: t('percentage'), value: 1, id: 'percent' },
  ];

  const displayCommissionOptions = [
    { name: t('commissionPerSale'), value: 0, id: 'sale' },
    { name: t('commissionPerPeriod'), value: 1, id: 'period' },
  ];

  const displayCommissionTable = [
    { name: t('calculateEntirePeriod'), value: 0, id: 'entire' },
    { name: t('calculateDailyCommission'), value: 1, id: 'daily' },
  ];

  const bonusesCommissionOptions = [
    { name: t('employeeBonus'), value: 'Personal', id: 'Personal' },
    { name: t('managerBonus'), value: 'Locations', id: 'Locations' },
  ];

  const fixedTypesMapIdToName: { [key: number]: string } = {
    0: 'flat',
    1: 'percent',
  };

  const fixedTypesMapNameToId: { [key: string]: number } = {
    flat: 0,
    percent: 1,
  };

  const preparePayload = () => {
    const {
      name, type, description, bonuses, bonusBase,
    } = selectedCommissionPlan;
    return {
      name, type, description, bonuses, bonusBase,
    };
  };

  const prepareFixedCommissionData = (payload: TPrePayload) => ({
    ...payload,
    fixedCommission: {
      fixedType: fixedTypesMapIdToName[selectedCommissionPlan.fixedType as number],
      fixedAmount: parseFloat(selectedCommissionPlan.fixedAmount as string),
    },
  });

  const prepareCommissionStepsData = (payload: TPrePayload) => ({
    ...payload,
    id: selectedCommissionPlan.id,
    commissionSteps: selectedCommissionPlan.commissionSteps,
  });

  const prepareProductCommissionData = (payload: TPrePayload) => ({
    ...payload,
    productCommissions: selectedCommissionPlan.productCommissions?.map((product) => ({
      title: product.title,
      displayName: product.displayName,
      variantId: product.id,
      sku: product.sku,
      fixedType: product.fixedType === 0 ? 'flat' : 'percent',
      fixedAmount: parseFloat(product.fixedAmount as unknown as string),
      imageUrl: product.image?.url,
      price: parseFloat(product.price),
      compareAtPrice: parseFloat(product.compareAtPrice as string),
    })),
  });

  const handlePlanMutation = async (data: Omit<TAddCommissionPlanReq['planData'], 'creator' & 'updatedBy'>) => {
    if (commissionMode === 'create') {
      await createPlanMutate(data);
    } else {
      await updatePlanMutate(data);
    }
  };

  return {
    createCommission,
    displayCommissionOptions,
    fixedTypesOptions,
    fixedTypesMapIdToName,
    fixedTypesMapNameToId,
    preparePayload,
    prepareFixedCommissionData,
    handlePlanMutation,
    prepareCommissionStepsData,
    isLoading: isCreateLoading || isUpdateLoading,
    displayCommissionTable,
    bonusesCommissionOptions,
    prepareProductCommissionData,
  };
}
